import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import type {
  GovernanceFormValues,
  GovernanceFilterType,
  GovernanceAllocationMethod,
} from "../types/Governance";
import {
  useV2ListResourcesForAccountQuery,
  useV2ListPhasesForAccountQuery,
  useV2ListServiceCategorysForAccountQuery,
  useV2GetAccountQuery,
} from "@generated";
import { useSelector } from "react-redux";
import type { RootState } from "@reducers/rootReducer";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import DropdownApi from "@components/FormsV2/DropdownApi/DropdownApi";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { CalculationTypeCards } from "@components/GovernanceEffort/CalculationTypeCards";
import { LoeAdjustmentsField } from "@components/GovernanceEffort/LoeAdjustmentsField";
import { useConfigurableGovernance } from "@components/GovernanceEffort/useConfigurableGovernance";
import ProjectVariablesV2 from "../../../ProjectVariables/ProjectVariablesV2";
import "./style.css";

const FILTER_TYPE_OPTIONS = [
  { value: "all_services", label: "All Services" },
  { value: "filter_by_lob", label: "Filter by Line of Business" },
  { value: "filter_by_phase", label: "Filter by Phase" },
];

const ALLOCATION_METHOD_OPTIONS = [
  { value: "prorate_phases_by_effort", label: "Prorate phases by effort" },
  { value: "select_phase", label: "Select phase" },
  { value: "select_category", label: "Select category" },
  { value: "project_management_phase", label: "Project management phase" },
];

export interface GovernanceFormProps {
  id: string;
  initialValues: GovernanceFormValues;
  onSubmit: (values: GovernanceFormValues) => void;
  isSubmitting?: boolean;
}

const toOption = (id: string | null, label: string | null) =>
  id == null
    ? { value: 0, label: "" }
    : { value: id, label: label ?? String(id) };

export const GovernanceForm = ({
  id,
  initialValues,
  onSubmit,
  isSubmitting: _isSubmitting,
}: GovernanceFormProps) => {
  const [values, setValues] = useState<GovernanceFormValues>(initialValues);
  const update = <K extends keyof GovernanceFormValues>(
    key: K,
    val: GovernanceFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: val }));

  const configurable = useConfigurableGovernance();
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data: account } = useV2GetAccountQuery({}, {
    skip: !accountSlug,
  });
  const categoryLabel =
    (account?.data?.attributes as any)?.["field-labels"]?.[
      "service_category"
    ] || "Category";

  const isPercent = values.calculationType === "percent_of_total";
  const showFilterId = values.filterType !== "all_services" && isPercent;

  return (
    <Form
      id={id}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(values);
      }}
    >
      <div className="adminFormSectionTitle">Description</div>
      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor="gov-name">Description</Form.Label>
          <TextField
            label={null}
            id="gov-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-3">
          <DropdownApi
            label="* Resource"
            value={toOption(values.resourceId, null)}
            onChange={(opt: any) =>
              update("resourceId", opt ? String(opt.value) : null)
            }
            getDataHook={useV2ListResourcesForAccountQuery}
            required
          />
        </div>
        <div className="col-sm-3 governanceToggleCell">
          <Form.Label>Is this governance required by all projects?</Form.Label>
          <GenericToggle
            key={`req-${initialValues.required}`}
            initialValueOfIsOn={values.required}
            onToggle={(v: boolean) => update("required", v)}
            onText="Required"
            offText="Optional"
            hasText
          />
        </div>
      </div>

      {/* The legacy form's user-defined fields sat under the Description row. */}
      <ProjectVariablesV2
        pvArrPassed={values.projectVariables}
        context="governance"
        setState={(variables) => update("projectVariables", variables)}
        has3PerRow
      />

      <div className="adminFormSectionTitle">Governance Effort</div>
      <CalculationTypeCards
        value={values.calculationType}
        onChange={(type) => update("calculationType", type)}
      />
      <div className="row">
        <div className="col-sm-6">
          {isPercent ? (
            <FormNumberFieldV2
              label="* Enter Governance Effort Percentage"
              id="gov-rate"
              placeholder="Enter percentage"
              value={values.rate == null ? "" : values.rate * 100}
              min={0}
              max={100}
              step={0.1}
              required
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                update(
                  "rate",
                  e.target.value === "" ? null : Number(e.target.value) / 100
                )
              }
            />
          ) : (
            <FormNumberFieldV2
              label="* Enter Fixed Governance Hours"
              id="gov-fixed-hours"
              placeholder="Enter hours"
              value={values.fixedHours ?? ""}
              min={configurable ? values.minimumHours ?? 0 : 0}
              step={0.01}
              required
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                update(
                  "fixedHours",
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            />
          )}
        </div>
        {configurable && (
          <div className="col-sm-6">
            <Form.Group className="formGroup">
              <Form.Label htmlFor="gov-minimum-hours">Minimum Hours</Form.Label>
              {/* Not FormNumberFieldV2: it renders 0 as blank. */}
              <Form.Control
                id="gov-minimum-hours"
                type="number"
                placeholder="0"
                min={0}
                step={0.01}
                value={values.minimumHours ?? ""}
                onChange={(e) =>
                  update(
                    "minimumHours",
                    e.target.value === "" ? null : Number(e.target.value)
                  )
                }
              />
              <small className="governanceFieldHelp">
                Governance never calculates below this, and fixed hours can't be
                set lower.
              </small>
            </Form.Group>
          </div>
        )}
      </div>
      {isPercent && (
        <>
          <div className="row">
            <div className="col-sm-6">
              <RequiredReactSelect
                label="* Which Services Impact Governance"
                value={
                  FILTER_TYPE_OPTIONS.find(
                    (o) => o.value === values.filterType
                  ) ?? FILTER_TYPE_OPTIONS[0]
                }
                onChange={(opt: any) =>
                  // Clear the picked id: a phase id is not a line of business.
                  setValues((prev) => ({
                    ...prev,
                    filterType: (opt?.value ??
                      "all_services") as GovernanceFilterType,
                    filterId: null,
                  }))
                }
                options={FILTER_TYPE_OPTIONS}
              />
            </div>
            {showFilterId && values.filterType === "filter_by_phase" && (
              <div className="col-sm-6">
                <DropdownApi
                  label="Select Phase"
                  value={toOption(
                    values.filterId == null ? null : String(values.filterId),
                    null
                  )}
                  onChange={(opt: any) =>
                    update("filterId", opt ? Number(opt.value) : null)
                  }
                  getDataHook={useV2ListPhasesForAccountQuery}
                />
              </div>
            )}
            {showFilterId && values.filterType === "filter_by_lob" && (
              <div className="col-sm-6">
                <DropdownApi
                  label="Select Category"
                  value={toOption(
                    values.filterId == null ? null : String(values.filterId),
                    null
                  )}
                  onChange={(opt: any) =>
                    update("filterId", opt ? Number(opt.value) : null)
                  }
                  getDataHook={useV2ListServiceCategorysForAccountQuery}
                  labelAttribute="nested-name"
                />
              </div>
            )}
          </div>
          {configurable && (
            <LoeAdjustmentsField
              rules={values.rateRules}
              onChange={(rules) => update("rateRules", rules)}
            />
          )}
        </>
      )}

      <div className="adminFormSectionTitle">
        Assign Governance Effort to the Project
      </div>
      <div className="row">
        <div className="col-sm-3">
          <RequiredReactSelect
            label="Allocate to Project Phases by"
            value={ALLOCATION_METHOD_OPTIONS.find(
              (o) => o.value === values.allocationMethod
            )}
            onChange={(opt: any) =>
              update(
                "allocationMethod",
                (opt?.value ?? null) as GovernanceAllocationMethod | null
              )
            }
            options={ALLOCATION_METHOD_OPTIONS}
            placeholder="Select…"
          />
        </div>
        {values.allocationMethod === "select_phase" && (
          <div className="col-sm-3">
            <DropdownApi
              label="Phase"
              value={toOption(values.phaseId, null)}
              onChange={(opt: any) =>
                update("phaseId", opt ? String(opt.value) : null)
              }
              getDataHook={useV2ListPhasesForAccountQuery}
            />
          </div>
        )}
        {values.allocationMethod === "select_category" && (
          <div className="col-sm-3">
            <DropdownApi
              label={`* ${categoryLabel}`}
              value={toOption(values.sectionId, null)}
              onChange={(opt: any) =>
                update("sectionId", opt ? String(opt.value) : null)
              }
              getDataHook={useV2ListServiceCategorysForAccountQuery}
              labelAttribute="nested-name"
              required
            />
          </div>
        )}
        <div className="col-sm-3 governanceToggleCell">
          <Form.Label>
            Assign effort to services or report separately?
          </Form.Label>
          <GenericToggle
            key={`aes-${initialValues.assignEffortToService}`}
            initialValueOfIsOn={values.assignEffortToService}
            onToggle={(v: boolean) => update("assignEffortToService", v)}
            onText="Assign to service"
            offText="Keep separate"
            hasText
          />
        </div>
      </div>
    </Form>
  );
};
